/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import AddIcon from '@mui/icons-material/Add';
import { Chip, Divider } from "@material-ui/core";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import BarChartIcon from "@material-ui/icons/BarChart";
import PeopleIcon from "@material-ui/icons/People";
import ScheduleIcon from "@material-ui/icons/Schedule";

import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid, Box } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    backgroundColor: theme.palette.background.default,
    border: "none",
    ...theme.scrollbarStyles,
  },

  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  title: {
    color: "white",
    fontWeight: "bold",
  },
  searchPaper: {
    padding: "4px 16px",
    display: "flex",
    alignItems: "center",
    borderRadius: "15px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.7)",
      opacity: 1,
    },
  },
  addButton: {
    background: theme.palette.background.paper,
    color: "#6517ab",
    borderRadius: "15px",
    padding: "10px 20px",
    fontWeight: "600",
    textTransform: "none",
    "&:hover": {
      background: theme.palette.type === 'light' ? "#f0f0f0" : "rgba(255, 255, 255, 0.1)",
    },
  },

  campaignCard: {
    borderRadius: "20px",
    transition: "all 0.3s ease",
    border: "none",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    background: theme.palette.background.paper,
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    },
  },

  cardContent: {
    padding: theme.spacing(3),
  },
  statusChip: {
    borderRadius: "10px",
    fontWeight: "600",
    marginBottom: theme.spacing(1),
  },
  iconButton: {
    borderRadius: "12px",
    margin: theme.spacing(0, 0.5),
    transition: "all 0.2s",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
}));

const Campaigns = () => {
  const theme = useTheme();
  const classes = useStyles();

  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    }

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA": return "Inativa";
      case "PROGRAMADA": return "Programada";
      case "EM_ANDAMENTO": return "Em Andamento";
      case "CANCELADA": return "Cancelada";
      case "FINALIZADA": return "Finalizada";
      default: return val;
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingCampaign && `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <div className={classes.headerContainer}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h5" className={classes.title}>
                  {i18n.t("campaigns.title")}
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.8 }}>
                  Gerencie e acompanhe o desempenho de suas campanhas.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item xs={12} sm={7}>
                    <Paper className={classes.searchPaper} elevation={0}>
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "rgba(255,255,255,0.7)" }} />
                      </InputAdornment>
                      <TextField
                        fullWidth
                        placeholder={i18n.t("campaigns.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                          disableUnderline: true,
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Button
                      startIcon={<AddIcon />}
                      fullWidth
                      variant="contained"
                      onClick={handleOpenCampaignModal}
                      className={classes.addButton}
                    >
                      {i18n.t("campaigns.buttons.add")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            <Grid container spacing={2}>
              {loading ? (
                <Grid item xs={12}>
                  <Typography align="center">Carregando...</Typography>
                </Grid>
              ) : (
                campaigns.map((campaign) => (
                  <Grid item xs={12} sm={6} md={4} key={campaign.id}>
                    <Card className={classes.campaignCard} elevation={0}>
                      <CardContent className={classes.cardContent}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="h6" color="textPrimary" style={{ fontWeight: 700 }}>
                            {campaign.name}
                          </Typography>
                          <Chip
                            label={formatStatus(campaign.status)}
                            className={classes.statusChip}
                            style={{
                              backgroundColor:
                                campaign.status === "FINALIZADA" ? (theme.palette.type === 'light' ? "#e8f5e9" : "rgba(46, 125, 50, 0.2)") :
                                  campaign.status === "EM_ANDAMENTO" ? (theme.palette.type === 'light' ? "#e3f2fd" : "rgba(25, 118, 210, 0.2)") :
                                    campaign.status === "CANCELADA" ? (theme.palette.type === 'light' ? "#ffebee" : "rgba(211, 47, 47, 0.2)") :
                                      campaign.status === "PROGRAMADA" ? (theme.palette.type === 'light' ? "#fff3e0" : "rgba(239, 108, 0, 0.2)") : theme.palette.background.default,
                              color:
                                campaign.status === "FINALIZADA" ? "#2e7d32" :
                                  campaign.status === "EM_ANDAMENTO" ? "#1976d2" :
                                    campaign.status === "CANCELADA" ? "#d32f2f" :
                                      campaign.status === "PROGRAMADA" ? "#ef6c00" : theme.palette.text.secondary,
                            }}
                          />
                        </Box>

                        <Box mt={2} mb={2}>
                          <Typography variant="body2" color="textSecondary" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <PeopleIcon fontSize="small" /> Lista: {campaign.contactListId ? campaign.contactList.name : "Não definida"}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                            <ScheduleIcon fontSize="small" /> Agendado: {datetimeToClient(campaign.scheduledAt)}
                          </Typography>
                        </Box>

                        <Divider style={{ margin: "16px 0" }} />

                        <Box display="flex" justifyContent="space-around">
                          <IconButton
                            onClick={() => history.push(`/campaign/${campaign.id}/report`)}
                            size="small"
                            className={classes.iconButton}
                            style={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}
                            title="Ver Relatório"
                          >
                            <BarChartIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditCampaign(campaign)}
                            className={classes.iconButton}
                            style={{ backgroundColor: "#f3e5f5", color: "#6517ab" }}
                            title="Editar"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingCampaign(campaign);
                            }}
                            className={classes.iconButton}
                            style={{ backgroundColor: "#ffebee", color: "#d32f2f" }}
                            title="Excluir"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Campaigns;
